import { useId, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Mtds, useAuth, type NewsArticle } from 'prove-common'
import { ReferenceDialog } from '../reference-data/ReferenceDialog'
import { newsApiError, newsUserMessage } from '../../utils/newsApi'
import { newsSummary } from '../../utils/newsArticle'

type NewsDeleteDialogProps = {
  article: NewsArticle
  onClose: () => void
  onDeleted?: () => void
}

/**
 * Render conditionally: mounting per confirmation keeps the previous failure
 * from leaking into the next attempt.
 */
export const NewsDeleteDialog = ({
  article,
  onClose,
  onDeleted
}: NewsDeleteDialogProps) => {
  const titleId = useId()
  const errorRef = useRef<HTMLHeadingElement>(null)
  const { authFetch, isAdmin } = useAuth()
  const queryClient = useQueryClient()
  const summary = newsSummary(article)
  const deleteNews = useMutation({
    retry: false,
    mutationFn: async () => {
      if (!isAdmin)
        throw new Error('Du må være administrator for å slette nyheter.')
      const result = await authFetch().DELETE('/news/{articleId}', {
        params: { path: { articleId: article.id } }
      })
      if (!result.response.ok || result.error) throw newsApiError(result.error)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['news'] })
      void queryClient.invalidateQueries({ queryKey: ['newsLatest'] })
      void queryClient.invalidateQueries({
        queryKey: ['newsArticle', article.id]
      })
      Mtds.toast.success('Artikkelen er slettet')
      onClose()
      onDeleted?.()
    },
    onError: () => setTimeout(() => errorRef.current?.focus())
  })

  const close = () => {
    if (!deleteNews.isPending) onClose()
  }

  return (
    <ReferenceDialog
      open
      aria-labelledby={titleId}
      closedby='closerequest'
      onClose={close}
      onCancel={(event) => {
        event.preventDefault()
        close()
      }}
    >
      <Mtds.Prose>
        <Mtds.Heading as='h2' id={titleId} data-size='sm'>
          Slett artikkelen?
        </Mtds.Heading>
        <p>«{summary}» slettes permanent. Dette kan ikke angres.</p>
        {deleteNews.isError && (
          <Mtds.Alert as='div' role='alert' data-color='danger'>
            <Mtds.Heading as='h3' data-size='xs' ref={errorRef} tabIndex={-1}>
              Artikkelen ble ikke slettet
            </Mtds.Heading>
            <p>{newsUserMessage(deleteNews.error)}</p>
          </Mtds.Alert>
        )}
        <Mtds.Flex>
          <Mtds.Button
            type='button'
            autoFocus
            data-variant='secondary'
            disabled={deleteNews.isPending}
            onClick={close}
          >
            Avbryt
          </Mtds.Button>
          <Mtds.Button
            type='button'
            data-variant='primary'
            data-color='danger'
            disabled={deleteNews.isPending}
            aria-busy={deleteNews.isPending}
            onClick={() => deleteNews.mutate()}
          >
            {deleteNews.isPending ? 'Sletter …' : 'Slett artikkel'}
          </Mtds.Button>
        </Mtds.Flex>
      </Mtds.Prose>
    </ReferenceDialog>
  )
}
